import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { toast } from 'sonner';
import { Camera, HandshakeIcon, ImageIcon, MoreHorizontal, UploadCloud } from 'lucide-react';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { TextAreaField } from '@/components/textarea-field';
import { SelectField } from '@/components/select-field';
import { FileDropZone } from '@/components/file-drop-zone';
import { TelegramIcon } from '@/components/icons/telegram-icon';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { AssignmentPicker } from '@/routes/console/_authenticated/_layout/internal/_layout/photos/-features/assignment-picker';
import { photoSubmissionSchema } from '@/schema/photo-submission-schema';
import { postPhotoSubmission } from '@/services/photo-submission';
import { fetchCampaignAssignments } from '@/services/campaign-assignment';
import { errorHandler } from '@/lib/utils';
const searchSchema = z.object({
    assignment: z.string().optional(),
});
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/photos/new')({
    component: NewPhotoPage,
    validateSearch: zodValidator(searchSchema),
});
function NewPhotoPage() {
    const { t } = useTranslation('internal');
    const navigate = Route.useNavigate();
    const { assignment: assignmentHint } = Route.useSearch();
    const { data: assignments = [] } = useQuery({
        queryKey: ['campaign-assignments'],
        queryFn: fetchCampaignAssignments,
    });
    const [imageFile, setImageFile] = React.useState();
    const form = useForm({
        resolver: yupResolver(photoSubmissionSchema),
        defaultValues: {
            assignmentPublicId: assignmentHint ?? '',
            submittedVia: 'telegram',
            notes: '',
        },
    });
    React.useEffect(() => {
        if (assignmentHint) {
            form.setValue('assignmentPublicId', assignmentHint);
        }
    }, [assignmentHint, form]);
    const { mutate, isPending } = useMutation({
        mutationFn: postPhotoSubmission,
        onSuccess: () => {
            toast.success(t('photos.toast.logged'));
            navigate({ to: '/console/internal/photos' });
        },
        onError: errorHandler,
    });
    const onSubmit = (data) => {
        if (!imageFile) {
            toast.error(t('photos.toast.missing_image'));
            return;
        }
        mutate({ data, file: imageFile });
    };
    const sourceIcons = {
        telegram: <TelegramIcon className="h-4 w-4"/>,
        whatsapp: <WhatsAppIcon className="h-4 w-4"/>,
        in_person: <HandshakeIcon className="h-4 w-4"/>,
        staff_upload: <UploadCloud className="h-4 w-4"/>,
        other: <MoreHorizontal className="h-4 w-4"/>,
    };
    const sourceOptions = ['telegram', 'whatsapp', 'in_person', 'staff_upload', 'other'].map((value) => ({
        value,
        label: td(t, `enum.submission_via.${value}`),
        icon: sourceIcons[value],
    }));
    return (<div className="mx-auto w-full max-w-5xl space-y-6 p-6">
            <div className="space-y-1 border-b pb-4">
                <h1 className="flex items-center gap-2 text-3xl font-semibold">
                    <Camera className="h-7 w-7"/>
                    {t('photos.page.new_title')}
                </h1>
                <p className="text-sm text-muted-foreground">{t('photos.page.new_desc')}</p>
            </div>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                        <Card className="border-border/60 lg:sticky lg:top-6 lg:self-start">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <ImageIcon className="h-4 w-4 text-muted-foreground"/>
                                    {t('photos.page.section_photo')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <FileDropZone images={imageFile ? [imageFile] : []} onDrop={(files) => setImageFile(files[0])} onUpload={(e) => e.target.files && setImageFile(e.target.files[0])} onRemove={() => setImageFile(undefined)}/>
                            </CardContent>
                        </Card>

                        <Card className="border-border/60">
                            <CardHeader>
                                <CardTitle className="text-base">{t('photos.page.section_where')}</CardTitle>
                                <CardDescription>{t('photos.page.section_where_desc')}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <FormField control={form.control} name="assignmentPublicId" render={({ field }) => (<FormItem>
                                            <FormLabel>{t('photos.field.assignment')}</FormLabel>
                                            <FormControl>
                                                <AssignmentPicker value={field.value ?? ''} onChange={field.onChange} assignments={assignments} disabled={isPending}/>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>)}/>
                                <SelectField control={form.control} name="submittedVia" label={t('photos.field.source')} options={sourceOptions} disabled={isPending}/>
                                <TextAreaField control={form.control} name="notes" label={t('photos.field.notes')} rows={8} className="min-h-[180px] resize-y" disabled={isPending}/>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="sticky bottom-0 -mx-6 border-t border-border/60 bg-background/80 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                        <div className="mx-auto flex max-w-5xl items-center justify-end gap-3">
                            <p className="mr-auto text-xs text-muted-foreground">
                                {imageFile ? imageFile.name : t('photos.toast.missing_image')}
                            </p>
                            <Button type="submit" size="lg" disabled={isPending}>
                                <Camera className="mr-2 h-4 w-4"/>
                                {t('photos.action.log')}
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>
        </div>);
}
